import _ from 'lodash';
import { Binding } from 'morearty';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

import AdminEditor from './lib/admin_editor';
import AdminTable from './lib/admin_table';
import AgentSelectContainer from './routing_groups/agent_select_container';
import BadgeDropdown from './lib/badge_dropdown';
import Button, { ButtonTypes } from 'components/common/button';
import DefaultEndpointSelect from './routing_groups/default_endpoint_select';
import getErrorMessage from 'components/lib/error_handler';
import LabelInput from 'components/lib/label_input';
import RoutingGroup from 'models/routing_group';

class RoutingGroups extends React.Component {
  constructor(props) {
    super(props);

    _.bindAll(this, [
      'compareRoutingGroups',
      'convertSlaMinutesToSeconds',
      'getRoutingGroupEditorTitle',
      'handleDefaultEndpointChange',
      'handleNameChange',
      'handleSlaChange',
      'onRoutingGroupCancel',
      'onRoutingGroupSave',
      'populateEditorFields',
      'renderAgents',
      'renderDefaultAddress',
      'renderRoutingGroupEditor',
      'renderSecondaryColumns',
      'routingGroupAsTableRow',
      'toggleDisabled',
      'updateSelectedAgents',
    ]);

    this.state = { newlyCreatedRoutingGroupName: null };
  }

  render() {
    return (
      <AdminTable
        binding={{
          agentProfiles: this.props.agentProfilesBinding,
          default: this.props.defaultBinding,
          root: this.props.rootBinding,
        }}
        className="routingGroups"
        editorClassName="routingGroup-editor"
        filterer={this.filterer}
        isCreatable
        itemAsRow={this.routingGroupAsTableRow}
        itemCompare={this.compareRoutingGroups}
        hasPendingResonse={id => this.props.isResponsePending(id)}
        hasPendingState={id => this.props.isSavePending(id)}
        onOpenEditor={this.populateEditorFields}
        optionalColumns={this.renderSecondaryColumns}
        renderEditor={this.renderRoutingGroupEditor}
        subjectName="inbox"
        pluralSubjectName="inboxes"
      />
    );
  }

  populateEditorFields(routingGroupId) {
    // always clear editor state
    this.setState({ routingGroup: {}, newlyCreatedRoutingGroupName: null, title: null });

    // new routingGroup
    if (!routingGroupId) {
      const slaSeconds = RoutingGroup.convertSecondsToMinutes(RoutingGroup.getDefaultSlaSeconds());
      this.setState({ routingGroup: { defaultEndpoints: {}, slaSeconds, agentIds: [] } });

      // existing routing group
    } else {
      const routingGroup = this.props.defaultBinding.get(routingGroupId).toJS();
      routingGroup.slaSeconds = RoutingGroup.convertSecondsToMinutes(routingGroup.slaSeconds);
      this.setState({ routingGroup, title: routingGroup.name });
    }
  }

  onRoutingGroupCancel(onClose) {
    const id = this.state.routingGroup.id;
    this.props.cancelRoutingGroup(id);

    onClose();
  }

  onRoutingGroupSave(onClose) {
    const routingGroupAttrs = _.cloneDeep(this.state.routingGroup);
    routingGroupAttrs.slaSeconds = this.convertSlaMinutesToSeconds(routingGroupAttrs.slaSeconds);

    routingGroupAttrs.id
      ? this.props.updateRoutingGroup(routingGroupAttrs)
      : this.props.createRoutingGroup(routingGroupAttrs);

    onClose('submitted');

    this.setState({ newlyCreatedRoutingGroupName: routingGroupAttrs.name, suppressErrors: false });
  }

  convertSlaMinutesToSeconds(slaMinutes) {
    return Math.round(Number(slaMinutes) * 60);
  }

  getRoutingGroupEditorTitle() {
    return this.state.title ? `Editing '${this.state.title}'` : 'Add an inbox';
  }

  renderRoutingGroupEditor(id, onCloseCallback) {
    return (
      <AdminEditor
        className="routingGroup-editor"
        formFields={this.renderEditorFields(id, onCloseCallback)}
        onCancel={this.onRoutingGroupCancel.bind(null, onCloseCallback)}
        onSave={this.onRoutingGroupSave.bind(null, onCloseCallback)}
        title={this.getRoutingGroupEditorTitle()}
      />
    );
  }

  onKeyDown(event, onCloseCallback) {
    event.key === 'Enter' ? this.onRoutingGroupSave(onCloseCallback) : this.setState({ suppressErrors: true });
  }

  handleNameChange(event) {
    this.setState({ routingGroup: { ...this.state.routingGroup, name: event.target.value } });
  }

  handleSlaChange(event) {
    this.setState({ routingGroup: { ...this.state.routingGroup, slaSeconds: event.target.value } });
  }

  handleDefaultEndpointChange(type, value = '') {
    this.setState({
      routingGroup: {
        ...this.state.routingGroup,
        defaultEndpoints: {
          ...this.state.routingGroup.defaultEndpoints,
          [type]: value,
        },
      },
    });
  }

  renderEditorFields(id, onCloseCallback) {
    const errors = this.props.getErrors(id);
    const suppressErrors = this.state.suppressErrors;
    const selectedAgentIds = this.state.routingGroup.agentIds;

    return [
      <ButtonWrapper key="enable-disable-button">
        <Button
          buttonType={this.state.routingGroup.disabled ? ButtonTypes.SECONDARY : ButtonTypes.PRIMARY}
          onClick={this.toggleDisabled}
        >
          {this.state.routingGroup.disabled ? 'Disabled' : 'Enabled'}
        </Button>
      </ButtonWrapper>,
      <LabelInput
        inputId="name"
        key="routingGroup-nameinput"
        errorString={getErrorMessage({ attr: 'name', errors, suppressErrors })}
        labelClass="routingGroup-editor-label"
        placeholder="Name"
      >
        <input
          className="routingGroup-editor-name routingGroup-editor-input"
          onKeyDown={event => this.onKeyDown(event, onCloseCallback)}
          onChange={this.handleNameChange}
          value={this.state.routingGroup.name || ''}
        />
      </LabelInput>,
      <LabelInput
        inputId="slaSeconds"
        key="routingGroup-slaSecondsInput"
        errorString={getErrorMessage({ attr: 'slaSeconds', errors, suppressErrors })}
        labelClass="routingGroup-editor-label"
        placeholder="SERVICE LEVEL AGREEMENT (in minutes)"
      >
        <input
          className="routingGroup-editor-slaSeconds routingGroup-editor-input"
          onKeyDown={event => this.onKeyDown(event, onCloseCallback)}
          onChange={this.handleSlaChange}
          value={this.state.routingGroup.slaSeconds}
        />
      </LabelInput>,
      _.map(this.props.enabledChannels, this.renderDefaultAddress),
      <AgentSelectContainer
        header="Add or Remove Agents"
        selectedAgentIds={selectedAgentIds}
        updateSelectedAgents={this.updateSelectedAgents}
        key="agent-select-container"
      />,
    ];
  }

  renderDefaultAddress({ label, type }) {
    return (
      <DefaultEndpointSelect
        key={`${type.toLowerCase()}EndpointSelect`}
        label={label}
        onChange={this.handleDefaultEndpointChange}
        type={type}
        value={this.state.routingGroup.defaultEndpoints[type]}
      />
    );
  }

  toggleDisabled() {
    const disabled = !this.state.routingGroup.disabled;
    this.setState({ routingGroup: { ...this.state.routingGroup, disabled } });
    this.props.trackToggleDisabled(this.state.routingGroup.id, disabled);
  }

  updateSelectedAgents(agentIds) {
    this.setState({ routingGroup: { ...this.state.routingGroup, agentIds } });
  }

  filterer(filterRegExp, item) {
    let name = item.name.replace(/\s/g, '');
    return filterRegExp ? filterRegExp.test(name) : true;
  }

  compareRoutingGroups(lhsImm, rhsImm) {
    return lhsImm
      .get('name')
      .toLowerCase()
      .localeCompare(rhsImm.get('name').toLowerCase());
  }

  routingGroupAsTableRow(routingGroupImm) {
    return {
      classNames: routingGroupImm.get('disabled') ? 'adminTable-row-disabled' : undefined,
      id: routingGroupImm.get('id'),
      name: routingGroupImm.get('name'),
      highlight: routingGroupImm.get('name') === this.state.newlyCreatedRoutingGroupName,
    };
  }

  renderSecondaryColumns(routingGroupImm) {
    return this.renderAgents(routingGroupImm.get('id'), routingGroupImm.get('agentIds'));
  }

  renderAgents(routingGroupId, agentIds) {
    const agents = [];

    agentIds.forEach(id => {
      const agentProfile = this.props.findAgentProfile(id);

      if (agentProfile && !agentProfile.isDisabled()) {
        agents.push({ id, name: this.getAgentName(agentProfile) });
      } else if (!agentProfile) {
        agents.push({ id, name: 'Loading...' });
      }
    });

    return (
      agents.length > 0 && (
        <BadgeDropdown
          badgeText={`${agents.length} ${agents.length === 1 ? 'agent' : 'agents'}`}
          binding={this.props.defaultBinding.sub(routingGroupId)}
          className="routingGroup-agents"
          items={agents}
        />
      )
    );
  }

  getAgentName(agentProfile) {
    return agentProfile.name || agentProfile.email;
  }
}

const ButtonWrapper = styled.div`
  margin-top: 16px;
  position: absolute;
  right: 48px;
  top: -80px;
`;

RoutingGroups.propTypes = {
  agentProfilesBinding: PropTypes.instanceOf(Binding).isRequired,
  cancelRoutingGroup: PropTypes.func.isRequired,
  createRoutingGroup: PropTypes.func.isRequired,
  defaultBinding: PropTypes.instanceOf(Binding).isRequired,
  findAgentProfile: PropTypes.func.isRequired,
  getErrors: PropTypes.func.isRequired,
  isResponsePending: PropTypes.func.isRequired,
  isSavePending: PropTypes.func.isRequired,
  rootBinding: PropTypes.instanceOf(Binding).isRequired,
  updateRoutingGroup: PropTypes.func.isRequired,
};

export default RoutingGroups;



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/admin/routing_groups.jsx